import { Badge, ButtonV2, Dots, Dropdown, Text } from '@cimplico/components';
import {
    ChevronRightIcon,
    EllipsisHorizontalIcon,
    FolderPlusIcon,
    PencilIcon,
    PlusCircleIcon,
    SquaresPlusIcon,
    TableCellsIcon,
} from '@heroicons/react/20/solid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentFocusProperty, selectRenamingProperty, selectUpdatingDescriptionProperty } from '../../store/selectors';
import { setUpdatingDescriptionPropertyId, editPropertyDetails, setCurrentFocusProperty } from '../../store/slice';
import {
    CWTSchemaList,
    CWTSchemaSection,
    CWTSchemaSectionGroup,
    CWTSchemaTable,
    ListType,
    SectionType,
    TableType,
} from '../../../../types/custom-worksheet-v2';
import EditInput from './EditInput';
import PropertyName from './PropertyName';

function getBadgeColour(
    type: ListType | SectionType | TableType
): 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'gray' | 'pink' | 'indigo' {
    switch (type) {
        case ListType.LIST: {
            return 'red';
        }
        case SectionType.SECTION_GROUP: {
            return 'yellow';
        }
        case SectionType.SECTION: {
            return 'indigo';
        }
        case TableType.TABLE: {
            return 'green';
        }
        default: {
            return 'gray';
        }
    }
}

type HeaderProps = {
    property: Pick<CWTSchemaList | CWTSchemaSectionGroup | CWTSchemaSection | CWTSchemaTable, 'id' | 'description' | 'name' | 'type'>;
    isCollapsed: boolean;
    onCollapse: (isCollapsed: boolean) => void;
    onDelete: () => void;
    onCreateField?: () => void;
    onCreateSection?: () => void;
    onCreateSectionGroup?: () => void;
    onCreateTable?: () => void;
    onCreateColumn?: () => void;
};

function Header({
    property,
    isCollapsed,
    onCollapse,
    onDelete,
    onCreateField,
    onCreateSection,
    onCreateSectionGroup,
    onCreateTable,
    onCreateColumn,
}: HeaderProps): JSX.Element {
    const dispatch = useDispatch();
    const currentRenamingProperty = useSelector(selectRenamingProperty);
    const currentUpdatingDescriptionProperty = useSelector(selectUpdatingDescriptionProperty);
    const currentFocusProperty = useSelector(selectCurrentFocusProperty);

    const isRenaming = currentRenamingProperty === property.id;
    const isUpdatingDescription = currentUpdatingDescriptionProperty === property.id;

    const iconStyle = 'text-gray-500 w-4 h-4 m-1';

    const [description, setDescription] = useState<string | undefined | null>(property.description);

    const outerDivRef = useRef<HTMLDivElement | null>(null);
    const chevronRef = useRef<HTMLDivElement | null>(null);
    const nameDivRef = useRef<HTMLDivElement | null>(null);
    const descriptionDivRef = useRef<HTMLDivElement | null>(null);
    const addColumnRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    /**
     * On escape key/cancel button click, revert property description changes
     * Clear error message and clear editing property state
     */
    const handleResetValue = useCallback(
        function handleResetValue(): void {
            setDescription(property.description);
            dispatch(setUpdatingDescriptionPropertyId(undefined));
        },
        [dispatch, property.description]
    );

    /**
     * Save new property description value
     * Validate new description value
     */
    const handleSaveDescription = useCallback(
        function handleSaveDescription(): void {
            if (!isUpdatingDescription) {
                return;
            }

            // If property doesn't previously have a description and the new description value is also empty
            if (!description?.trim() && !property.description) {
                setDescription(property.description);
                dispatch(setUpdatingDescriptionPropertyId(undefined));
                return;
            }

            // Update description
            if (description?.trim() !== (property.description || '')) {
                dispatch(editPropertyDetails({ id: `${property.id}.description`, value: description ? description.trim() : null }));
            }
            dispatch(setUpdatingDescriptionPropertyId(undefined));
        },
        [description, property.description, property.id, isUpdatingDescription, dispatch]
    );

    const handleClickOutside = useCallback(
        function handleClickOutside(event: MouseEvent): void {
            if (!(event.target instanceof Element) || (property.type !== TableType.TABLE && property.type !== SectionType.SECTION)) {
                return;
            }

            if (outerDivRef?.current && (outerDivRef.current.contains(event.target) || outerDivRef.current.isEqualNode(event.target))) {
                // Don't trigger open side panel if click event happens when property name is in editing state
                if (isRenaming) {
                    return;
                }

                const withinChevronRef =
                    chevronRef?.current && (chevronRef.current.contains(event.target) || chevronRef.current.isEqualNode(event.target));
                const withinNameDiv =
                    nameDivRef?.current && (nameDivRef.current.contains(event.target) || nameDivRef.current.isEqualNode(event.target));
                const withinDescriptionDiv =
                    descriptionDivRef?.current &&
                    (descriptionDivRef.current.contains(event.target) || descriptionDivRef.current.isEqualNode(event.target));
                const withinAddColumnDiv =
                    addColumnRef?.current &&
                    (addColumnRef.current.contains(event.target) || addColumnRef.current.isEqualNode(event.target));
                const withinDropdownDiv =
                    dropdownRef?.current && (dropdownRef.current.contains(event.target) || dropdownRef.current.isEqualNode(event.target));

                // Don't trigger open side panel if click event is within property name/button
                if (withinChevronRef || withinNameDiv || withinDescriptionDiv || withinAddColumnDiv || withinDropdownDiv) {
                    return;
                }

                if (currentFocusProperty === property.id) {
                    dispatch(setCurrentFocusProperty(undefined));
                } else {
                    dispatch(setCurrentFocusProperty(property.id));
                }
            }
        },
        [currentFocusProperty, dispatch, property.id, isRenaming]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return (): void => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    return (
        <div
            ref={outerDivRef}
            className={`p-2 flex gap-2 items-center ${
                property.type === SectionType.SECTION || property.type === TableType.TABLE ? '' : 'rounded-lg border'
            } cursor-pointer border-gray-300`}
        >
            <div className="flex grow gap-1 items-center">
                <Dots className={iconStyle} />
                <div ref={chevronRef}>
                    <ChevronRightIcon
                        className={`${isCollapsed ? '' : 'rotate-90'} transition-all w-5 h-5 text-gray-500 cursor-pointer`}
                        onClick={(): void => onCollapse(!isCollapsed)}
                    />
                </div>
                <div className="flex flex-col min-w-[200px]">
                    <PropertyName id={property.id} fieldName={property.name} ref={nameDivRef} />
                    <div ref={descriptionDivRef}>
                        {isUpdatingDescription && (
                            <EditInput
                                placeholder="Enter a description..."
                                value={description || undefined}
                                onChange={setDescription}
                                onCancel={handleResetValue}
                                onSave={handleSaveDescription}
                            />
                        )}
                        {!isUpdatingDescription && property.description && (
                            <div
                                className="cursor-pointer hover:bg-gray-200 py-0.5 pl-3 pr-2 rounded-[5px] flex gap-3 items-center group border border-transparent"
                                onClick={(): void => {
                                    setDescription(property.description);
                                    dispatch(setUpdatingDescriptionPropertyId(property.id));
                                }}
                            >
                                <Text lines={1} className="text-gray-500 text-sm">
                                    {property.description}
                                </Text>
                                <PencilIcon className="w-3 h-3 hidden group-hover:block" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {onCreateField && (
                <div ref={addColumnRef}>
                    <ButtonV2 type="hollow" size="sm" label="Add field" onClick={onCreateField} leftIcon={<PlusCircleIcon />} />
                </div>
            )}
            {onCreateColumn && (
                <div ref={addColumnRef}>
                    <ButtonV2 type="hollow" size="sm" label="Add column" onClick={onCreateColumn} leftIcon={<PlusCircleIcon />} />
                </div>
            )}
            {onCreateSection && (
                <ButtonV2 type="hollow" size="sm" label="Add section" onClick={onCreateSection} leftIcon={<FolderPlusIcon />} />
            )}
            {onCreateSectionGroup && (
                <ButtonV2 type="hollow" size="sm" label="Add section group" onClick={onCreateSectionGroup} leftIcon={<SquaresPlusIcon />} />
            )}
            {onCreateTable && <ButtonV2 type="hollow" size="sm" label="Add table" onClick={onCreateTable} leftIcon={<TableCellsIcon />} />}
            <div className="flex gap-2 items-center">
                <Badge shape="squared" label={property.type} color={getBadgeColour(property.type)} size="small" />
                <div ref={dropdownRef}>
                    <Dropdown size="small" leftIcon={<EllipsisHorizontalIcon className="text-gray-500 w-4 h-4" />}>
                        {!property.description && (
                            <Dropdown.Option
                                label="Add description"
                                onClick={(): void => {
                                    dispatch(setUpdatingDescriptionPropertyId(property.id));
                                }}
                            />
                        )}
                        <Dropdown.Option label="Delete" onClick={onDelete} />
                    </Dropdown>
                </div>
            </div>
        </div>
    );
}

export default Header;
